import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { constantCase } from 'constant-case';
import FormWidget from '../../FormWidget';
import EligibilityAgentButton from '../../Eligibility/EligibilityAgentButton';
import RequestRefreshDialog from '../../RequestRefreshDialog';
import store from '../../../redux/store/configureStore';
import { initApp } from '../../../redux/actions/init.actions';
import * as PatientTypeService from '../../../services/patientType.service';
import * as ServiceTypeService from '../../../services/serviceType.service';
import * as ProviderService from '../../../services/provider.service';
import * as RequestVerificationTypesService from '../../../services/verificationTypes.service';
import CoordinationTypeService from '../../../services/coordinationType.service';
import RequestLinkTypeService from '../../../services/requestLinkType.service';
import {
  fetchPatientTypesSuccess,
  fetchServiceTypesSuccess,
  preloadProviderCapabilities,
} from '../../../redux/actions/service.actions';
import CallTypeService from '../../../services/callTypeService';
import EmsFacilityTypeService from '../../../services/emsFacilityType.service';
import RequestCancelReasonTypeService from '../../../services/requestCancelReasonTypes.service';
import PayerTypeService from '../../../services/payerType.service';
import OrdersService from '../../../services/orders/orders.service';
import AmbCircularProgress from '../../common/AmbCircularProgress';
import RequestTypeService from '../../../services/requestType.service';
import { theme } from '../../../theme';
import { VERSION } from '../../../version';
import { connect } from 'react-redux';
import { setUserFacilities } from '../../../redux/actions/user.actions';

const Version = styled.p`
  font-weight: 400;
  font-size: 12px;
  opacity: 0.5;
  background-color: inherit;
  margin: 0;
  padding: 8px 0 8px 0;
`;

const Container = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

async function createEnum(fn, nameProp = 'name', idProp = 'id') {
  const array = await fn();

  return array.reduce((acc, element) => {
    acc[constantCase(element[nameProp])] = element[idProp];

    return acc;
  }, {});
}

async function loadTypeTables(user) {
  const [
    callTypes,
    transportReasons,
    coordinationTypes,
    emsFacilityTypes,
    cancelReasonTypes,
    payerTypes,
    requestTypes,
    requestVerificationTypes,
    requestLinkTypes,
  ] = await Promise.all([
    createEnum(async () => CallTypeService.get(user.id)),
    OrdersService.getTransportReasons(true, true),
    createEnum(CoordinationTypeService.getCoordinationTypes),
    createEnum(EmsFacilityTypeService.getEmsFacilityTypes),
    createEnum(RequestCancelReasonTypeService.fetchCancelReasonTypes),
    createEnum(PayerTypeService.fetchPayerTypes),
    createEnum(RequestTypeService.fetchRequestTypes),
    createEnum(RequestVerificationTypesService.getRequestVerificationTypes),
    createEnum(RequestLinkTypeService.getRequestLinkTypes, 'name', 'name'),
  ]);

  Object.assign(window.ambulnzConfig, {
    callTypes,
    transportReasons,
    coordinationTypes,
    emsFacilityTypes,
    cancelReasonTypes,
    payerTypes,
    requestTypes,
    requestVerificationTypes,
    requestLinkTypes,
  });
}

const SideContent = ({ user }) => {
  const [sideContentLoadedState, setSideContentLoadedState] = useState(false);

  const loadAppState = async () => {
    store.dispatch(initApp());
    const account = store.getState().userAccount.selectedUserAccount;
    await loadTypeTables(account);
    store.dispatch(setUserFacilities());

    const [providerCapabilityResult, patientTypeResult, serviceTypeResult] = await Promise.all([
      ProviderService.getProviders(),
      PatientTypeService.getPatientTypes(),
      ServiceTypeService.getServiceTypes(),
    ]);
    store.dispatch(fetchPatientTypesSuccess(patientTypeResult));
    store.dispatch(fetchServiceTypesSuccess(serviceTypeResult));
    store.dispatch(preloadProviderCapabilities(providerCapabilityResult));
  };

  const removeSpinner = () => {
    const spinner = document.getElementById('spinner-div');
    // IE11 way to remove element from DOM
    spinner && spinner.parentNode.removeChild(spinner);
  };

  useEffect(() => {
    if (!sideContentLoadedState) {
      removeSpinner();
      loadAppState().then(() => setSideContentLoadedState(true));
    }
  }, [sideContentLoadedState]);

  const formContent = () => {
    if (sideContentLoadedState) {
      return <FormWidget />;
    }

    return (
      <Container>
        <AmbCircularProgress
          propsStyles={{
            position: 'absolute',
            top: 250,
            left: 240,
            zIndex: 1,
          }}
        />
      </Container>
    );
  };

  return (
    <div style={{ height: '100%', position: 'relative', paddingTop: 1, width: 'auto' }}>
      <div style={{ backgroundColor: theme.palette.divider, paddingLeft: 10, paddingRight: 10 }}>
        {formContent()}
        <Version>v{VERSION}</Version>
      </div>
      {window.ambulnzConfig?.constants?.ELIGIBILITY_MODE === 'agent' && user.isDispatcher && (
        <>
          <EligibilityAgentButton />
          <RequestRefreshDialog />
        </>
      )}
    </div>
  );
};

SideContent.propTypes = {
  user: PropTypes.shape({
    isDispatcher: PropTypes.bool,
  }).isRequired,
};

const mapStateToProps = state => ({
  user: state.user.model,
});

export default connect(mapStateToProps)(SideContent);
